import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Boxes,
  Clock,
  CreditCard,
  DollarSign,
  Package,
  PackageCheck,
  Plus,
  Settings,
  Store,
  TrendingUp,
  Wallet,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Earnings = {
  grossSales: number;
  platformCommission: number;
  refunds: number;
  withdrawals: number;
  netEarnings: number;
  availableBalance: number;
  pendingBalance: number;
};

export function VendorHomePage() {
  const [earnings, setEarnings] = useState<Earnings | null>(null);
  const [orderCount, setOrderCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [vendor, setVendor] = useState<{ businessName: string; status: string; commissionRate: number } | null>(null);

  useEffect(() => {
    void apiGet<{ vendor: any }>("/api/vendors/me")
      .then((d) => setVendor(d?.vendor ?? null))
      .catch(() => {});
    void apiGet<Earnings>("/api/vendors/me/earnings")
      .then((d) => setEarnings(d))
      .catch(() => {});
    void apiGet<{ vendorOrders: unknown[] }>("/api/vendors/me/orders")
      .then((d) => setOrderCount(d?.vendorOrders?.length ?? 0))
      .catch(() => {});
    void apiGet<{ products: unknown[] }>("/api/vendors/me/products")
      .then((d) => setProductCount(d?.products?.length ?? 0))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Review Banner if application is PENDING */}
      {vendor?.status === "PENDING" && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/90 p-4 sm:p-5 dark:border-amber-900/40 dark:bg-amber-950/40">
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <Clock className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-amber-900 dark:text-amber-200 text-sm sm:text-base">
                Vendor Application Under Review
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
                Your store <strong>{vendor?.businessName}</strong> has been registered and is under review by MOVA administrators. In the meantime, you can customize your store profile and prepare product submissions.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 sm:gap-3">
                <Link
                  to="/vendor/products/new"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#087DB5] hover:bg-[#073B70] px-3 py-2 text-xs font-bold text-white shadow-xs transition active:scale-95"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Submit Product for Review
                </Link>
                <Link
                  to="/vendor/products"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-white/90 dark:bg-slate-900 px-3 py-2 text-xs font-bold text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition active:scale-95 shadow-2xs"
                >
                  <Package className="h-3.5 w-3.5" />
                  Submitted Products ({productCount})
                </Link>
                <Link
                  to="/vendor/settings"
                  className="inline-flex items-center gap-1 text-xs font-bold text-amber-900 dark:text-amber-200 underline hover:no-underline px-1 py-1"
                >
                  <Store className="h-3.5 w-3.5" />
                  Store Settings →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Store Header Card with Quick Mobile Actions */}
      <div className="amazon-card p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg sm:text-2xl font-black text-slate-900 dark:text-white truncate">
                {vendor?.businessName ?? "Your Store Dashboard"}
              </h1>
              <Badge tone={vendor?.status === "ACTIVE" ? "success" : "prime"}>
                {vendor?.status ?? "ACTIVE"}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Platform Commission Rate: <strong className="text-slate-700 dark:text-slate-200">{vendor?.commissionRate ?? 0}%</strong> · Seller Portal
            </p>
          </div>

          {/* Quick Action CTAs */}
          <div className="flex flex-wrap items-center gap-2 pt-1 sm:pt-0">
            <Link
              to="/vendor/products/new"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#087DB5] hover:bg-[#073B70] px-3.5 py-2 text-xs font-bold text-white shadow-xs transition active:scale-95"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Product</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 transition active:scale-95 shadow-2xs"
              title="Open public storefront"
            >
              <Store className="h-3.5 w-3.5 text-amazon-gold" />
              <span className="hidden sm:inline">Storefront</span>
              <ArrowUpRight className="h-3 w-3 text-slate-400" />
            </Link>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid - Mobile-First 2-col to 4-col */}
      <div>
        <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5 px-0.5">
          Store Performance
        </h2>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-4">
          <StatCard
            label="Gross sales"
            value={formatPrice(earnings?.grossSales ?? 0)}
            icon={DollarSign}
            iconColor="text-blue-600 bg-blue-50 dark:bg-blue-950/60 dark:text-blue-400"
          />
          <StatCard
            label="Net earnings"
            value={formatPrice(earnings?.netEarnings ?? 0)}
            icon={TrendingUp}
            iconColor="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/60 dark:text-emerald-400"
            highlight
          />
          <StatCard
            label="Available"
            value={formatPrice(earnings?.availableBalance ?? 0)}
            icon={Wallet}
            iconColor="text-teal-600 bg-teal-50 dark:bg-teal-950/60 dark:text-teal-400"
          />
          <StatCard
            label="Orders"
            value={String(orderCount)}
            icon={PackageCheck}
            iconColor="text-indigo-600 bg-indigo-50 dark:bg-indigo-950/60 dark:text-indigo-400"
            link="/vendor/orders"
          />
          <StatCard
            label="Products"
            value={String(productCount)}
            icon={Boxes}
            iconColor="text-purple-600 bg-purple-50 dark:bg-purple-950/60 dark:text-purple-400"
            link="/vendor/products"
          />
          <StatCard
            label="Commission"
            value={formatPrice(earnings?.platformCommission ?? 0)}
            icon={CreditCard}
            iconColor="text-amber-600 bg-amber-50 dark:bg-amber-950/60 dark:text-amber-400"
          />
          <StatCard
            label="Refunds"
            value={formatPrice(earnings?.refunds ?? 0)}
            icon={ArrowUpRight}
            iconColor="text-rose-600 bg-rose-50 dark:bg-rose-950/60 dark:text-rose-400"
          />
          <StatCard
            label="Pending payout"
            value={formatPrice(earnings?.pendingBalance ?? 0)}
            icon={Clock}
            iconColor="text-orange-600 bg-orange-50 dark:bg-orange-950/60 dark:text-orange-400"
          />
        </div>
      </div>

      {/* Quick Navigation Cards on Mobile & Desktop */}
      <div>
        <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5 px-0.5">
          Quick Shortcuts
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4">
          <Link
            to="/vendor/products"
            className="amazon-card p-3 sm:p-4 hover:border-[#087DB5]/40 transition group active:scale-98"
          >
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 mb-2 group-hover:scale-105 transition-transform">
              <Boxes className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <p className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white">Product Catalog</p>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">Manage stock & prices</p>
          </Link>

          <Link
            to="/vendor/orders"
            className="amazon-card p-3 sm:p-4 hover:border-[#087DB5]/40 transition group active:scale-98"
          >
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 mb-2 group-hover:scale-105 transition-transform">
              <PackageCheck className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <p className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white">Customer Orders</p>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">Dispatch & tracking</p>
          </Link>

          <Link
            to="/vendor/withdrawals"
            className="amazon-card p-3 sm:p-4 hover:border-[#087DB5]/40 transition group active:scale-98"
          >
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-950/60 dark:text-teal-400 mb-2 group-hover:scale-105 transition-transform">
              <Wallet className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <p className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white">Withdrawals</p>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">Request store payout</p>
          </Link>

          <Link
            to="/vendor/settings"
            className="amazon-card p-3 sm:p-4 hover:border-[#087DB5]/40 transition group active:scale-98"
          >
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 mb-2 group-hover:scale-105 transition-transform">
              <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <p className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white">Store Settings</p>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">Profile, logo & contact</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  iconColor,
  highlight,
  link,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  iconColor: string;
  highlight?: boolean;
  link?: string;
}) {
  const content = (
    <div
      className={`amazon-card p-3 sm:p-4 flex flex-col justify-between transition-all hover:border-[#087DB5]/40 ${
        highlight ? "ring-1 ring-emerald-500/30" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-1 mb-2">
        <p className="text-[10px] sm:text-xs font-bold uppercase text-slate-500 dark:text-slate-400 truncate">
          {label}
        </p>
        <div className={`flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-lg ${iconColor}`}>
          <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        </div>
      </div>
      <p
        className={`text-sm sm:text-xl font-black truncate tracking-tight ${
          highlight ? "text-emerald-600 dark:text-emerald-400" : "text-slate-900 dark:text-white"
        }`}
        title={value}
      >
        {value}
      </p>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block active:scale-98 transition-transform">
        {content}
      </Link>
    );
  }

  return content;
}

